import React, { useState, useEffect, useRef } from 'react';
import { RefreshCw, Search, Eye, ChevronDown, ChevronUp, DollarSign, BarChart3, TrendingUp, Percent, Filter, Database, X, Loader2, CheckCircle, AlertTriangle } from 'lucide-react';
import { api } from '../../api/client';
import MetricCard from '../../components/MetricCard';
import VeeqoSyncModal from '../../components/Shared/VeeqoSyncModal';

// Debounce helper function
const debounce = (func, wait) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

// COGS Cell Component with visual status indicators
const COGSCell = ({ value, status, sku }) => {
  const getStyle = () => {
    switch (status) {
      case 'missing':
        return {
          textColor: 'text-red-500',
          bgColor: 'bg-red-500/10',
          icon: <AlertTriangle className="w-3 h-3" />,
          tooltip: `Fix master data - Add COGS for SKU: ${sku || 'Unknown'}`
        };
      case 'zero':
        return {
          textColor: 'text-amber-500',
          bgColor: 'bg-amber-500/10',
          icon: null,
          tooltip: 'Product exists but COGS is $0.00'
        };
      case 'valid':
      default:
        return {
          textColor: 'text-emerald-400',
          bgColor: '',
          icon: null,
          tooltip: null
        };
    }
  };

  const style = getStyle();
  const displayValue = typeof value === 'number' ? (Number(value) || 0).toFixed(2) : '0.00';

  return (
    <div 
      className={`flex items-center justify-end gap-1 px-1.5 py-0.5 rounded ${style.bgColor}`}
      title={style.tooltip}
    >
      {style.icon && <span className={style.textColor}>{style.icon}</span>}
      <span className={`${style.textColor} ${status === 'missing' ? 'font-semibold' : ''}`}>
        ${displayValue}
      </span>
    </div>
  );
};

// Margin Cell Component with visual status indicators
const MarginCell = ({ margin, cogsStatus }) => {
  if (cogsStatus === 'missing') {
    return (
      <div 
        className="flex items-center justify-end gap-1 text-red-500 font-semibold"
        title="Margin unreliable - COGS missing from Product Master"
      >
        <AlertTriangle className="w-3 h-3" />
        <span>{(Number(margin) || 0).toFixed(1)}%</span>
      </div>
    );
  }
  
  // Normal margin display with color coding
  const getMarginColor = (m) => {
    if (m === null || m === undefined) return 'text-gray-400';
    if (m < 0) return 'text-red-500';
    if (m < 15) return 'text-amber-500';
    if (m < 30) return 'text-yellow-400';
    return 'text-emerald-400';
  };

  return (
    <span className={getMarginColor(margin)}>
      {(Number(margin) || 0).toFixed(1)}%
    </span>
  );
};

const VeeqoOrders = ({ dateRange: parentDateRange }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);  // NEW: Search loading state
  const [dateFilter, setDateFilter] = useState(parentDateRange || 'last30days'); // Use parent prop if available
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [missingCogsCount, setMissingCogsCount] = useState(0); // Track orders with missing COGS
  
  // Sync progress state
  const [syncProgress, setSyncProgress] = useState({
    is_syncing: false,
    progress: 0,
    synced_orders: 0,
    total_estimated: 0,
    message: ''
  });
  const [syncComplete, setSyncComplete] = useState(false);
  const [newOrdersCount, setNewOrdersCount] = useState(0);
  const pollIntervalRef = useRef(null);
  
  // Summary stats
  const [stats, setStats] = useState({
    totalOrders: 0,
    totalProfit: 0,
    avgMargin: 0,
    mostProfitableSKU: null,
    highestCOGSProduct: null,
    lossMakingOrders: 0
  });
  
  // Filters
  const [profitStatusFilter, setProfitStatusFilter] = useState('all');
  const [cogsMin, setCogsMin] = useState('');
  const [cogsMax, setCogsMax] = useState('');
  const [marginMin, setMarginMin] = useState('');
  const [marginMax, setMarginMax] = useState('');
  
  // Sorting
  const [sortBy, setSortBy] = useState('profit_desc');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  // Fetch last sync time
  const fetchLastSyncTime = async () => {
    try {
      const response = await api.get('/api/v1/veeqo/smart-sync-status', {
        headers: { 'X-Tenant-ID': 'tenant-123' }
      });
      if (response.last_successful_sync) {
        setLastSync(new Date(response.last_successful_sync));
      }
      // Also update stats with total from DB
      if (response.total_orders && stats.totalOrders === 0) {
        setStats(prev => ({ ...prev, totalOrders: response.total_orders }));
      }
    } catch (error) {
      // Fallback to old endpoint
      try {
        const fallback = await api.get('/api/v1/veeqo/sync-status', {
          headers: { 'X-Tenant-ID': 'tenant-123' }
        });
        if (fallback.last_sync?.sync_date) {
          setLastSync(new Date(fallback.last_sync.sync_date));
        }
      } catch (e) {
        console.error('Error fetching sync status:', e);
      }
    }
  };

  // Fetch sync progress
  const fetchSyncProgress = async () => {
    try {
      const response = await api.get('/api/v1/veeqo/sync-progress');
      setSyncProgress(response);
      
      if (response.is_syncing) {
        setSyncing(true);
        setSyncComplete(false);
      } else if (syncing && !response.is_syncing) {
        // Sync just completed
        setSyncing(false);
        setSyncComplete(true);
        setNewOrdersCount(response.synced_orders - stats.totalOrders);
        
        // Clear polling
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        
        // Refresh orders after completion
        await fetchOrders();
        
        // Hide completion message after 5 seconds
        setTimeout(() => setSyncComplete(false), 5000);
      }
      
      return response;
    } catch (error) {
      console.error('Error fetching sync progress:', error);
      return null;
    }
  };

  // Start polling for sync progress
  const startProgressPolling = () => {
    if (pollIntervalRef.current) return;
    
    pollIntervalRef.current = setInterval(async () => {
      const progress = await fetchSyncProgress();
      if (progress && !progress.is_syncing) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    }, 2000); // Poll every 2 seconds
  };

  // Helper to get date range from preset
  const getDateRange = (filter) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    let startDate, endDate;
    
    switch (filter) {
      case 'today':
        startDate = today;
        endDate = new Date(today.getTime() + 24 * 60 * 60 * 1000);
        break;
      case 'yesterday':
        startDate = new Date(today.getTime() - 24 * 60 * 60 * 1000);
        endDate = today;
        break;
      case 'last7days':
        startDate = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
        endDate = new Date(today.getTime() + 24 * 60 * 60 * 1000);
        break;
      case 'last30days':
        startDate = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
        endDate = new Date(today.getTime() + 24 * 60 * 60 * 1000);
        break;
      case 'custom':
        // Use manual dateFrom/dateTo if set
        if (dateFrom) startDate = new Date(dateFrom);
        else startDate = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
        if (dateTo) endDate = new Date(dateTo + 'T23:59:59');
        else endDate = new Date(today.getTime() + 24 * 60 * 60 * 1000);
        break;
      default:
        startDate = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
        endDate = new Date(today.getTime() + 24 * 60 * 60 * 1000);
    }
    
    return {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0]
    };
  };

  const fetchOrders = async (filter = dateFilter, searchQuery = '') => {
    setLoading(true);
    if (searchQuery) setIsSearching(true);
    console.log('📦 fetchOrders called with:', { filter, searchQuery });
    try {
      const { startDate, endDate } = getDateRange(filter);
      
      // Build params object
      // NOTE: Don't pass status - backend defaults to profitable statuses
      // (shipped, completed, dispatched, delivered) to match Veeqo Profit Analyzer
      const params = { 
        page: 1, 
        page_size: 500,
        start_date: startDate,
        end_date: endDate
        // status is NOT passed - backend uses PROFITABLE_STATUSES by default
      };
      
      // Add search parameter if provided
      if (searchQuery && searchQuery.trim()) {
        params.search = searchQuery.trim();
        console.log('🔍 Adding search param:', searchQuery.trim());
      }
      
      console.log('📋 API params:', params);
      
      // Fetch orders with date filter and search from API
      const response = await api.get('/api/v1/veeqo/orders', { params });
      
      console.log('📥 API response:', { total: response.total_count, orders: response.orders?.length });
      
      if (response.orders) {
        setOrders(response.orders);
        setLastSync(response.synced_at);
        // Calculate stats immediately with the fetched orders
        calculateStats(response.orders);
      }
    } catch (error) {
      console.error('❌ Error fetching Veeqo orders:', error);
    } finally {
      setLoading(false);
      setIsSearching(false);
    }
  };
  
  // Handle search input change - trigger API call
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    
    // Trigger search after a short delay
    if (window.searchTimeout) {
      clearTimeout(window.searchTimeout);
    }
    
    window.searchTimeout = setTimeout(() => {
      if (value && value.trim().length >= 2) {
        console.log('🔍 Triggering search for:', value);
        setIsSearching(true);
        fetchOrders(dateFilter, value);
      } else if (!value || value.trim() === '') {
        console.log('🔍 Clearing search');
        fetchOrders(dateFilter, '');
      }
    }, 400);
  };
  
  // Handle Enter key for immediate search
  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter' && searchTerm.trim()) {
      console.log('🔍 Enter key search for:', searchTerm);
      if (window.searchTimeout) {
        clearTimeout(window.searchTimeout);
      }
      setIsSearching(true);
      fetchOrders(dateFilter, searchTerm);
    }
  };

  // Map parent dateRange format to local dateFilter format
  const mapDateRangeToFilter = (range) => {
    const mapping = {
      'today': 'today',
      'yesterday': 'yesterday',
      'last_7_days': 'last7days',
      'last_30_days': 'last30days',
      'last_90_days': 'last30days',  // Fallback to 30 days for 90 days
      'custom': 'custom'
    };
    return mapping[range] || 'last30days';
  };

  // Sync with parent dateRange when it changes
  useEffect(() => {
    if (parentDateRange) {
      const mappedFilter = mapDateRangeToFilter(parentDateRange);
      if (mappedFilter !== dateFilter) {
        setDateFilter(mappedFilter);
      }
    }
  }, [parentDateRange]);

  // Re-fetch orders when date filter changes (preserve search term)
  useEffect(() => {
    fetchOrders(dateFilter, searchTerm);
    fetchLastSyncTime(); // Also refresh sync status
  }, [dateFilter]);

  // Fetch last sync time on mount (with current search term preserved)
  useEffect(() => {
    fetchLastSyncTime();
    fetchOrders(dateFilter, searchTerm);
  }, []);

  const handleSyncStarted = () => {
    setSyncing(true);
    setSyncComplete(false);
    setSyncProgress({
      is_syncing: true,
      progress: 0,
      synced_orders: 0,
      total_estimated: 0,
      message: 'Starting sync...'
    });
    startProgressPolling();
  };

  const calculateStats = (orderList) => {
    const totalOrders = orderList.length;
    const totalProfit = orderList.reduce((sum, order) => sum + (order.gross_profit || 0), 0);
    const avgMargin = orderList.length > 0 
      ? orderList.reduce((sum, order) => sum + (order.profit_margin || 0), 0) / orderList.length 
      : 0;
    const lossMakingOrders = orderList.filter(order => (order.profit_margin || 0) < 0).length;
    
    // Count orders with missing COGS - check status or infer from cogs_source
    const missingCogs = orderList.filter(order => {
      if (order.cogs_status === 'missing') return true;
      const lineItem = order.line_items?.[0] || {};
      if (lineItem.cogs_status === 'missing') return true;
      const cogsSource = lineItem.cogs_source || '';
      return cogsSource.includes('Not found') || cogsSource === '';
    }).length;
    setMissingCogsCount(missingCogs);
    
    // Find most profitable SKU
    let mostProfitable = null;
    let maxMargin = -Infinity;
    orderList.forEach(order => {
      const margin = order.profit_margin || 0;
      const lineItem = order.line_items?.[0] || {};
      const sellable = lineItem.sellable || {};
      const sku = sellable.sku_code || lineItem.sku_code;
      
      if (margin > maxMargin && sku) {
        maxMargin = margin;
        mostProfitable = { sku, margin, product: sellable.product_title || lineItem.product_title || 'N/A' };
      }
    });
    
    // Find highest COGS product
    let highestCOGS = null;
    let maxCOGS = -Infinity;
    orderList.forEach(order => {
      const lineItem = order.line_items?.[0] || {};
      const sellable = lineItem.sellable || {};
      const cogs = lineItem.cogs_per_unit || sellable.cost_price || lineItem.cost_price || 0;
      const sku = sellable.sku_code || lineItem.sku_code;
      
      if (cogs > maxCOGS && sku) {
        maxCOGS = cogs;
        highestCOGS = { sku, cogs, product: sellable.product_title || lineItem.product_title || 'N/A' };
      }
    });
    
    setStats({ totalOrders, totalProfit, avgMargin: (Number(avgMargin) || 0).toFixed(1), mostProfitableSKU: mostProfitable, highestCOGSProduct: highestCOGS, lossMakingOrders });
  };
  
  const getProfitStatus = (margin) => {
    if (margin >= 50) return { label: 'High', color: 'green' };
    if (margin >= 20) return { label: 'Good', color: 'yellow' };
    if (margin >= 0) return { label: 'Low', color: 'orange' };
    return { label: 'Loss', color: 'red' };
  };
  
  const getRowColor = (margin) => {
    if (margin >= 20) return 'bg-green-900/20 hover:bg-green-800/30';
    if (margin >= 0) return 'bg-yellow-900/20 hover:bg-yellow-800/30';
    return 'bg-red-900/20 hover:bg-red-800/30';
  };

  useEffect(() => {
    fetchSyncProgress(); // Check if sync is in progress on mount
    
    // Cleanup polling on unmount
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);
  
  useEffect(() => {
    setCurrentPage(1);
  }, [profitStatusFilter, cogsMin, cogsMax, marginMin, marginMax, dateFrom, dateTo, sortBy]);
  
  // Filter and sort orders
  // Note: Search filtering is now done server-side via API, so we skip client-side search filter
  const filteredAndSortedOrders = orders.filter(order => {
    const margin = order.profit_margin || 0;
    const lineItem = order.line_items?.[0] || {};
    const sellable = lineItem.sellable || {};
    const cogs = lineItem.cogs_per_unit || sellable.cost_price || lineItem.cost_price || 0;
    
    // NOTE: Search filter removed - now handled server-side via API
    // This allows searching across ALL orders, not just the first 500

    // Profit status filter (client-side since it's quick)
    if (profitStatusFilter !== 'all') {
      if (profitStatusFilter === 'profitable' && margin < 20) return false;
      if (profitStatusFilter === 'low_margin' && (margin < 0 || margin >= 20)) return false;
      if (profitStatusFilter === 'loss' && margin >= 0) return false;
    }

    // COGS/Margin filters (client-side)
    if (cogsMin && cogs < parseFloat(cogsMin)) return false;
    if (cogsMax && cogs > parseFloat(cogsMax)) return false;
    if (marginMin && margin < parseFloat(marginMin)) return false;
    if (marginMax && margin > parseFloat(marginMax)) return false;

    // Date filter (only for custom range with manual dateFrom/dateTo)
    // Note: Other date presets are filtered server-side
    if (dateFilter === 'custom') {
      if (dateFrom && order.created_at && new Date(order.created_at) < new Date(dateFrom)) return false;
      if (dateTo && order.created_at && new Date(order.created_at) > new Date(dateTo + 'T23:59:59')) return false;
    }

    return true;
  }).sort((a, b) => {
    const [field, order] = sortBy.split('_');
    const getValue = (o) => {
      if (field === 'profit') return o.gross_profit || 0;
      if (field === 'margin') return o.profit_margin || 0;
      if (field === 'date') return new Date(o.created_at).getTime();
      return 0;
    };
    return order === 'asc' ? getValue(a) - getValue(b) : getValue(b) - getValue(a);
  });
  
  // Recalculate stats when filtered orders change (for client-side filters only)
  // Note: Search is now server-side, stats come from API response
  useEffect(() => {
    if (profitStatusFilter !== 'all' || cogsMin || cogsMax || marginMin || marginMax) {
      calculateStats(filteredAndSortedOrders);
    }
  }, [filteredAndSortedOrders.length, profitStatusFilter, cogsMin, cogsMax, marginMin, marginMax]);
  
  // Pagination
  const totalFilteredOrders = filteredAndSortedOrders.length;
  const totalPages = Math.ceil(totalFilteredOrders / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedOrders = filteredAndSortedOrders.slice(startIndex, startIndex + itemsPerPage);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '$0.00';
    return `$${Number(amount).toFixed(2)}`;
  };

  const clearFilters = () => {
    setSearchTerm('');
    setProfitStatusFilter('all');
    setCogsMin('');
    setCogsMax('');
    setMarginMin('');
    setMarginMax('');
    setDateFrom('');
    setDateTo('');
    setSortBy('profit_desc');
    // Re-fetch orders without search term
    fetchOrders(dateFilter, '');
  };

  return (
    <div className="space-y-3">
      {/* Compact Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-bold text-white flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-green-400" />
            Profit Analysis
          </h2>
          <p className="text-[10px] text-gray-400">
            {stats.totalOrders} orders • Last sync: {lastSync ? formatDate(lastSync) : 'Never'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Refresh Button */}
          <button 
            onClick={() => fetchOrders(dateFilter)} 
            disabled={loading}
            className={`px-3 py-1.5 rounded text-xs flex items-center gap-1.5 transition-colors ${
              loading 
                ? 'bg-gray-700 text-gray-400 cursor-not-allowed' 
                : 'bg-slate-700 text-white hover:bg-slate-600'
            }`}
          >
            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          
          {/* Sync Button */}
          <button 
            onClick={() => setShowSyncModal(true)} 
            disabled={syncing}
            className={`px-3 py-1.5 rounded text-xs flex items-center gap-1.5 transition-colors ${
              syncing 
                ? 'bg-blue-800 text-blue-200 cursor-not-allowed' 
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {syncing ? (
              <>
                <Loader2 className="w-3 h-3 animate-spin" />
                Syncing...
              </>
            ) : (
              <>
                <Database className="w-3 h-3" />
                Sync
              </>
            )}
          </button>
        </div>
      </div>

      {/* Sync Progress Bar */}
      {syncing && syncProgress.is_syncing && (
        <div className="bg-slate-800 rounded-lg p-3 border border-blue-500/30">
          <div className="flex justify-between items-center text-xs text-gray-400 mb-2">
            <span className="flex items-center gap-2">
              <Loader2 className="w-3 h-3 animate-spin text-blue-400" />
              {syncProgress.message || 'Syncing orders from Veeqo...'}
            </span>
            <span className="text-blue-400 font-medium">
              {syncProgress.progress}% ({syncProgress.synced_orders.toLocaleString()}/{syncProgress.total_estimated.toLocaleString()})
            </span>
          </div>
          <div className="w-full bg-slate-700 rounded-full h-2 overflow-hidden">
            <div 
              className="bg-blue-500 h-2 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${syncProgress.progress}%` }}
            />
          </div>
        </div>
      )}

      {/* Sync Complete Message */}
      {syncComplete && (
        <div className="bg-green-900/30 border border-green-500/50 rounded-lg p-3 flex items-center gap-2 text-sm text-green-400">
          <CheckCircle className="w-4 h-4" />
          <span>
            ✅ Sync complete! {newOrdersCount > 0 ? `${newOrdersCount} new orders added.` : 'All orders up to date.'}
          </span>
        </div>
      )}

      {/* Missing COGS Warning Banner */}
      {missingCogsCount > 0 && (
        <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-400" />
            <span className="text-red-300 text-sm">
              <strong>{missingCogsCount} orders</strong> have missing COGS data. Profit analysis may be incomplete.
            </span>
          </div>
          <a 
            href="/products?filter=needs-cogs"
            className="bg-red-500/80 hover:bg-red-500 text-white text-xs px-3 py-1.5 rounded transition-colors"
          >
            Fix in Product Master →
          </a>
        </div>
      )}

      {/* Veeqo Sync Modal */}
      <VeeqoSyncModal
        isOpen={showSyncModal}
        onClose={() => setShowSyncModal(false)}
        onSyncComplete={async (result) => {
          // Refresh orders, stats, and last sync time after sync completes
          await fetchOrders(dateFilter);
          await fetchLastSyncTime();
          setSyncing(false);
          setSyncComplete(true);
          setNewOrdersCount(result?.new_orders || 0);
          
          // Hide completion message after 5 seconds
          setTimeout(() => setSyncComplete(false), 5000);
        }}
        dateRange={(() => {
          const { startDate, endDate } = getDateRange(dateFilter);
          return { start: startDate, end: endDate };
        })()}
      />

      {/* Compact Metric Cards - Same as Hub style */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <MetricCard
          icon={<TrendingUp className="w-4 h-4 text-yellow-400" />}
          label="TOP SKU"
          value={stats.mostProfitableSKU?.sku?.substring(0, 12) || 'N/A'}
          subtitle={stats.mostProfitableSKU ? `${(Number(stats.mostProfitableSKU.margin) || 0).toFixed(0)}% margin` : 'no data'}
          status={stats.mostProfitableSKU?.margin > 40 ? 'green' : 'default'}
        />
        <MetricCard
          icon={<DollarSign className="w-4 h-4 text-green-400" />}
          label="HIGH COGS"
          value={stats.highestCOGSProduct?.sku?.substring(0, 12) || 'N/A'}
          subtitle={stats.highestCOGSProduct ? formatCurrency(stats.highestCOGSProduct.cogs) : 'no data'}
          status="default"
        />
        <MetricCard
          icon={<BarChart3 className="w-4 h-4 text-blue-400" />}
          label="AVG MARGIN"
          value={`${stats.avgMargin}%`}
          subtitle={`${stats.totalOrders} orders`}
          status={parseFloat(stats.avgMargin) > 35 ? 'green' : parseFloat(stats.avgMargin) > 20 ? 'yellow' : 'red'}
        />
        <MetricCard
          icon={<Percent className="w-4 h-4 text-red-400" />}
          label="LOSSES"
          value={stats.lossMakingOrders.toString()}
          subtitle="need attention"
          status={stats.lossMakingOrders === 0 ? 'green' : stats.lossMakingOrders < 5 ? 'yellow' : 'red'}
        />
      </div>

      {/* Compact Filters */}
      <div className="bg-slate-800 rounded-lg p-3">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xs font-semibold text-white flex items-center gap-1.5">
            <Filter className="w-3.5 h-3.5 text-blue-400" />
            Filters
          </h3>
          <button onClick={clearFilters} className="text-[10px] text-blue-400 hover:text-blue-300">
            Clear All
          </button>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-2">
          {/* Profit Status */}
          <select
            value={profitStatusFilter}
            onChange={(e) => setProfitStatusFilter(e.target.value)}
            className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-xs text-white"
          >
            <option value="all">All Orders</option>
            <option value="profitable">🟢 Profitable (≥20%)</option>
            <option value="low_margin">🟡 Low Margin (0-20%)</option>
            <option value="loss">🔴 Loss-making</option>
          </select>
          
          {/* Sort By */}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-xs text-white"
          >
            <option value="profit_desc">Profit $ (High→Low)</option>
            <option value="profit_asc">Profit $ (Low→High)</option>
            <option value="margin_desc">Margin % (High→Low)</option>
            <option value="margin_asc">Margin % (Low→High)</option>
            <option value="date_desc">Date (Newest)</option>
            <option value="date_asc">Date (Oldest)</option>
          </select>
          
          {/* Search */}
          <div className="relative">
            {isSearching ? (
              <Loader2 className="absolute left-2 top-1/2 transform -translate-y-1/2 w-3 h-3 text-blue-400 animate-spin" />
            ) : (
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-3 h-3 text-gray-500" />
            )}
            <input
              type="text"
              placeholder="Search order #, SKU, customer..."
              value={searchTerm}
              onChange={handleSearchChange}
              onKeyDown={handleSearchKeyDown}
              className="w-full bg-slate-900 border border-slate-700 rounded pl-7 pr-2 py-1.5 text-xs text-white placeholder-gray-500"
            />
            {searchTerm && (
              <button
                onClick={() => { setSearchTerm(''); fetchOrders(dateFilter, ''); }}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-white"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
          
          {/* Advanced Toggle */}
          <button 
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="flex items-center justify-center gap-1 text-[10px] text-blue-400 hover:text-blue-300 bg-slate-900 border border-slate-700 rounded px-2 py-1.5"
          >
            Advanced {showAdvanced ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
        </div>
        
        {/* Advanced Filters - Collapsible */}
        {showAdvanced && (
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-2 mt-2 pt-2 border-t border-slate-700">
            <input
              type="number"
              placeholder="Min COGS $"
              value={cogsMin}
              onChange={(e) => setCogsMin(e.target.value)}
              className="bg-slate-900 border border-slate-700 rounded px-2 py-1 text-xs text-white placeholder-gray-500"
            />
            <input
              type="number"
              placeholder="Max COGS $"
              value={cogsMax}
              onChange={(e) => setCogsMax(e.target.value)}
              className="bg-slate-900 border border-slate-700 rounded px-2 py-1 text-xs text-white placeholder-gray-500"
            />
            <input
              type="number"
              placeholder="Min Margin %"
              value={marginMin}
              onChange={(e) => setMarginMin(e.target.value)}
              className="bg-slate-900 border border-slate-700 rounded px-2 py-1 text-xs text-white placeholder-gray-500"
            />
            <input
              type="number"
              placeholder="Max Margin %"
              value={marginMax}
              onChange={(e) => setMarginMax(e.target.value)}
              className="bg-slate-900 border border-slate-700 rounded px-2 py-1 text-xs text-white placeholder-gray-500"
            />
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="bg-slate-900 border border-slate-700 rounded px-2 py-1 text-xs text-white"
            />
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="bg-slate-900 border border-slate-700 rounded px-2 py-1 text-xs text-white"
            />
          </div>
        )}
      </div>

      {/* Compact Table */}
      <div className="bg-slate-800 rounded-lg overflow-hidden">
        <div className="px-3 py-2 border-b border-slate-700 flex items-center justify-between">
          <span className="text-xs text-gray-400">
            Showing {startIndex + 1}-{Math.min(startIndex + itemsPerPage, totalFilteredOrders)} of {totalFilteredOrders}
          </span>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-gray-500">Per page:</span>
            <select 
              value={itemsPerPage} 
              onChange={(e) => setItemsPerPage(Number(e.target.value))}
              className="bg-slate-900 border border-slate-700 rounded px-1.5 py-0.5 text-xs text-white"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </div>
        </div>
        
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="w-5 h-5 animate-spin text-blue-400" />
            <span className="ml-2 text-xs text-gray-400">Loading orders...</span>
          </div>
        ) : paginatedOrders.length === 0 ? (
          <div className="text-center py-8">
            <Database className="w-8 h-8 text-gray-500 mx-auto mb-2" />
            <p className="text-sm text-gray-400">No orders match your filters</p>
            <button onClick={clearFilters} className="text-xs text-blue-400 hover:text-blue-300 mt-2">
              Clear filters
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-900">
                <tr>
                  <th className="px-3 py-2 text-left text-[10px] uppercase text-gray-400 font-semibold">Order</th>
                  <th className="px-3 py-2 text-left text-[10px] uppercase text-gray-400 font-semibold">SKU</th>
                  <th className="px-3 py-2 text-left text-[10px] uppercase text-gray-400 font-semibold hidden lg:table-cell">Product</th>
                  <th className="px-3 py-2 text-center text-[10px] uppercase text-gray-400 font-semibold">Qty</th>
                  <th className="px-3 py-2 text-right text-[10px] uppercase text-orange-400 font-semibold">COGS</th>
                  <th className="px-3 py-2 text-right text-[10px] uppercase text-blue-400 font-semibold">Price</th>
                  <th className="px-3 py-2 text-right text-[10px] uppercase text-green-400 font-semibold">Profit</th>
                  <th className="px-3 py-2 text-right text-[10px] uppercase text-green-400 font-semibold">Margin</th>
                  <th className="px-3 py-2 text-center text-[10px] uppercase text-gray-400 font-semibold hidden md:table-cell">Ship Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700">
                {paginatedOrders.map((order) => {
                  const lineItem = order.line_items?.[0] || {};
                  const sellable = lineItem.sellable || {};
                  const margin = order.profit_margin || 0;
                  const profitStatus = getProfitStatus(margin);
                  const quantity = lineItem.quantity || 0;
                  const cogsPerUnit = lineItem.cogs_per_unit || sellable.cost_price || 0;
                  const cogs = cogsPerUnit * quantity;
                  const pricePerUnit = lineItem.price_per_unit || sellable.price || 0;
                  const price = pricePerUnit * quantity;
                  const profit = order.gross_profit || 0;
                  const sku = sellable.sku_code || lineItem.sku_code || 'N/A';
                  const productTitle = sellable.product_title || lineItem.product_title || 'N/A';
                  
                  // Determine COGS status - check order level first, then line item, then infer
                  let cogsStatus = order.cogs_status || lineItem.cogs_status;
                  if (!cogsStatus) {
                    // Infer status from cogs_source field
                    const cogsSource = lineItem.cogs_source || '';
                    if (cogsSource.includes('Not found') || cogsSource === '') {
                      cogsStatus = 'missing';
                    } else if (cogsPerUnit === 0) {
                      cogsStatus = 'zero';
                    } else {
                      cogsStatus = 'valid';
                    }
                  }
                  
                  return (
                    <tr key={order.id} className={`${getRowColor(margin)} transition-colors`}>
                      <td className="px-3 py-2 text-xs font-medium text-blue-300">{order.number}</td>
                      <td className="px-3 py-2 text-xs font-mono text-white">{sku}</td>
                      <td className="px-3 py-2 text-xs text-gray-300 truncate max-w-[150px] hidden lg:table-cell" title={productTitle}>
                        {productTitle.substring(0, 25)}{productTitle.length > 25 ? '...' : ''}
                      </td>
                      <td className="px-3 py-2 text-xs text-center text-white">{quantity}</td>
                      <td className="px-3 py-2 text-xs text-right">
                        <COGSCell value={cogs} status={cogsStatus} sku={sku} />
                      </td>
                      <td className="px-3 py-2 text-xs text-right text-blue-300 font-medium">{formatCurrency(price)}</td>
                      <td className={`px-3 py-2 text-xs text-right font-bold ${profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {formatCurrency(profit)}
                      </td>
                      <td className="px-3 py-2 text-xs text-right font-bold">
                        <MarginCell margin={margin} cogsStatus={cogsStatus} />
                      </td>
                      <td className="px-3 py-2 text-xs text-center text-gray-400 hidden md:table-cell">
                        {formatDate(order.shipped_at || order.created_at)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-3 py-2 border-t border-slate-700 flex items-center justify-between">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-2 py-1 text-xs bg-slate-700 text-white rounded disabled:opacity-50 hover:bg-slate-600"
            >
              ← Prev
            </button>
            <span className="text-xs text-gray-400">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-2 py-1 text-xs bg-slate-700 text-white rounded disabled:opacity-50 hover:bg-slate-600"
            >
              Next →
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default VeeqoOrders;
